import React, { useEffect, useState, useMemo, memo, useRef } from 'react'

import {
    useTheme,
    Paper,
    Typography,
    CircularProgress,
    FormControlLabel,
    FormControl,
    RadioGroup,
    Grid,
    Radio,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Switch,
    IconButton,
    Tooltip,
    Divider,
} from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'

import { Container } from '../../../../components'
import { axiosInstance } from '../../../../helpers'
import { useAutoScheduleStyles } from './styles'
import {
    CommonAreaRule,
    BathAreaRule,
    ListVendor,
    TemplateUnit,
    ezScheduleDetails,
    Folder,
    Service,
    DoubleTransferChainResponse,
    Unit,
} from '../../../../models'
import { useFinderSelection } from '../../../../hooks'
import { usePopover } from '../../../../hooks/usePopover'
import { SelectorPopover } from '../../../../components/TemplateScheduleGantt/Components/SelectorPopover'
import { ServiceRow } from './components/ServiceRow'
import { UnitSelector } from './components/UnitSelector'
import { DoubleTransferChainModal } from '../DoubleTransferChainModal'
import { useTimezone } from '../../../../contexts/timezone'
import AddIcon from '@material-ui/icons/Add'
import EditIcon from '@material-ui/icons/Edit'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import SettingsIcon from '@material-ui/icons/Settings'
import { DateTime } from 'luxon'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import { Lock, Public, ErrorOutline } from '@material-ui/icons'

import { SchedulePhase } from './types'
import {
    UpdateEzScheduleParams,
    AddTemplateServiceParams,
    DeleteTemplateServiceParams,
    AddTemplateServiceVendorParams,
    DeleteTemplateServiceVendorParams,
    UpdateTemplateServiceVendorParams,
    SetTemplateUnitsParams,
    UpdateTemplateServiceParams,
} from '../../../../hooks/useEzSchedule/types'

interface Props {
    ezSchedule: ezScheduleDetails
    phaseId?: number
    ezScheduleLoading: boolean
    infrastructure: Folder
    vendorList: ListVendor[]
    serviceList: Service[]
    onPhaseChange: (phase: SchedulePhase) => void
    updateEzSchedule: (params: UpdateEzScheduleParams) => void
    addTemplateService: (params: AddTemplateServiceParams) => void
    deleteTemplateService: (params: DeleteTemplateServiceParams) => void
    addTemplateServiceVendor: (params: AddTemplateServiceVendorParams) => void
    deleteTemplateServiceVendor: (
        params: DeleteTemplateServiceVendorParams,
    ) => void
    updateTemplateServiceVendor: (
        params: UpdateTemplateServiceVendorParams,
    ) => void
    setTemplateUnits: (params: SetTemplateUnitsParams) => void
    updateTemplateService: (params: UpdateTemplateServiceParams) => void
    generateTemplateWorkorders: () => void
    refetchTemplateWorkorders: () => void
}

const MemoizedServiceRow = memo(ServiceRow)

interface RuleOptionDetail {
    label: string
    description: string
}

const COMMON_AREA_RULE_DETAILS: Record<CommonAreaRule, RuleOptionDetail> = {
    [CommonAreaRule.NO_RULE]: {
        label: 'No Rule',
        description: 'Common area status is set independently',
    },
    [CommonAreaRule.ANY_VACANT]: {
        label: 'Any Vacant',
        description:
            'If any bedroom is Vacant, TI, or TO, common area is Vacant',
    },
    [CommonAreaRule.ANY_RENEWAL]: {
        label: 'Any Renewal',
        description: 'If any bedroom is Renewal, common area is Renewal',
    },
    [CommonAreaRule.MAJORITY_VACANT]: {
        label: 'Majority Vacant',
        description:
            'If 50% or more bedrooms are Vacant, TI, or TO, common area is Vacant',
    },
}

const BATH_AREA_RULE_DETAILS: Record<BathAreaRule, RuleOptionDetail> = {
    [BathAreaRule.NO_RULE]: {
        label: 'No Rule',
        description: 'Bath area status is set independently',
    },
    [BathAreaRule.MIRROR_BED]: {
        label: 'Mirror Bed',
        description:
            'If a bath area is mapped to a bed, set the bath area to the same status as the bed',
    },
}

export const AutoSchedule = (props: Props) => {
    const {
        ezSchedule,
        phaseId,
        ezScheduleLoading,
        infrastructure,
        vendorList,
        serviceList,
        onPhaseChange,
        updateEzSchedule,
        addTemplateService,
        deleteTemplateService,
        addTemplateServiceVendor,
        deleteTemplateServiceVendor,
        updateTemplateServiceVendor,
        setTemplateUnits,
        updateTemplateService,
    } = props

    const classes = useAutoScheduleStyles()
    const theme = useTheme()

    const { currentTimezone } = useTimezone()

    const { finderSelection, setSelection } = useFinderSelection('schedule')

    const selectedPhase = useMemo(() => {
        if (!ezSchedule || phaseId === undefined) return null
        return (
            ezSchedule.phases.find((phase) => phase.phase?.id === phaseId) ??
            null
        )
    }, [ezSchedule, phaseId])

    // Parse dates from the selected phase (or base schedule when phaseId is not set)
    const workStartDate = useMemo((): DateTime | null => {
        const sourceWorkStartDate =
            phaseId === undefined
                ? ezSchedule?.work_start_date
                : selectedPhase?.work_start_date

        if (sourceWorkStartDate) {
            const parsed = DateTime.fromISO(sourceWorkStartDate).setZone(
                currentTimezone,
            )
            if (parsed.isValid) return parsed
        }
        return null
    }, [phaseId, ezSchedule?.work_start_date, selectedPhase, currentTimezone])

    const workEndDate = useMemo((): DateTime | null => {
        const sourceWorkEndDate =
            phaseId === undefined
                ? ezSchedule?.work_end_date
                : selectedPhase?.work_end_date

        if (sourceWorkEndDate) {
            const parsed = DateTime.fromISO(sourceWorkEndDate).setZone(
                currentTimezone,
            )
            if (parsed.isValid) return parsed
        }
        return null
    }, [phaseId, ezSchedule?.work_end_date, selectedPhase, currentTimezone])

    const transferStartDate = useMemo((): DateTime | undefined => {
        const sourceTransferStartDate = selectedPhase?.transfer_start_date
        if (sourceTransferStartDate) {
            return DateTime.fromISO(sourceTransferStartDate).setZone(
                currentTimezone,
            )
        }
        return undefined
    }, [selectedPhase])

    const transferEndDate = useMemo((): DateTime | undefined => {
        const sourceTransferEndDate = selectedPhase?.transfer_end_date
        if (sourceTransferEndDate) {
            return DateTime.fromISO(sourceTransferEndDate).setZone(
                currentTimezone,
            )
        }
        return undefined
    }, [selectedPhase])

    const destUnitReadyDate = useMemo((): DateTime | undefined => {
        const source = selectedPhase?.dest_unit_ready_date
        if (source) {
            return DateTime.fromISO(source).setZone(currentTimezone)
        }
        return undefined
    }, [selectedPhase])

    const dtUnitReadyDate = useMemo((): DateTime | undefined => {
        const source = selectedPhase?.dt_unit_ready_date
        if (source) {
            return DateTime.fromISO(source).setZone(currentTimezone)
        }
        return undefined
    }, [selectedPhase])

    // Parse schedule bounds for validation
    const scheduleStartDate = useMemo((): DateTime | null => {
        if (ezSchedule?.start_date) {
            const parsed = DateTime.fromISO(ezSchedule.start_date).setZone(
                currentTimezone,
            )
            if (parsed.isValid) return parsed
        }
        return null
    }, [ezSchedule?.start_date, currentTimezone])

    // Seed the date-picker's initial month/day without auto-selecting a required value.
    const workStartInitialFocusedDate = useMemo((): DateTime | null => {
        if (workStartDate) return workStartDate
        return scheduleStartDate
    }, [workStartDate, scheduleStartDate])

    const scheduleEndDate = useMemo((): DateTime | null => {
        if (ezSchedule?.end_date) {
            const parsed = DateTime.fromISO(ezSchedule.end_date).setZone(
                currentTimezone,
            )
            if (parsed.isValid) return parsed
        }
        return null
    }, [ezSchedule?.end_date, currentTimezone])

    // Seed the date-picker's initial month/day without auto-selecting a required value.
    const workEndInitialFocusedDate = useMemo((): DateTime | null => {
        if (workEndDate) return workEndDate
        return scheduleEndDate
    }, [workEndDate, scheduleEndDate])

    // Seed transfer pickers without auto-selecting required values.
    const transferStartInitialFocusedDate = useMemo((): DateTime | null => {
        if (transferStartDate) return transferStartDate
        if (workStartDate) return workStartDate.plus({ days: 1 })
        return scheduleStartDate
    }, [transferStartDate, workStartDate, scheduleStartDate])

    const transferEndInitialFocusedDate = useMemo((): DateTime | null => {
        if (transferEndDate) return transferEndDate
        if (workEndDate) return workEndDate
        return scheduleEndDate
    }, [transferEndDate, workEndDate, scheduleEndDate])

    // Date validation errors
    const dateValidationErrors = useMemo(() => {
        const errors: {
            workStart?: string
            workEnd?: string
            transferStart?: string
            transferEnd?: string
            destUnitReady?: string
            dtUnitReady?: string
        } = {}

        // work_start_date: must be >= schedule.start_date AND < schedule.end_date
        if (
            workStartDate &&
            scheduleStartDate &&
            workStartDate < scheduleStartDate
        ) {
            errors.workStart = 'Must be on or after schedule start date'
        }
        if (
            workStartDate &&
            scheduleEndDate &&
            workStartDate >= scheduleEndDate
        ) {
            errors.workStart = 'Must be before schedule end date'
        }

        // work_end_date: must be > work_start_date AND <= schedule.end_date
        if (workEndDate && workStartDate && workEndDate <= workStartDate) {
            errors.workEnd = 'Must be after work start date'
        }
        if (workEndDate && scheduleEndDate && workEndDate > scheduleEndDate) {
            errors.workEnd = 'Must be on or before schedule end date'
        }

        // transfer_start_date: must be >= work_start_date AND < work_end_date
        if (
            transferStartDate &&
            workStartDate &&
            transferStartDate < workStartDate
        ) {
            errors.transferStart = 'Must be on or after work start date'
        }
        if (
            transferStartDate &&
            workEndDate &&
            transferStartDate >= workEndDate
        ) {
            errors.transferStart = 'Must be before work end date'
        }

        // transfer_end_date: must be > transfer_start_date AND < work_end_date
        if (
            transferEndDate &&
            transferStartDate &&
            transferEndDate <= transferStartDate
        ) {
            errors.transferEnd = 'Must be after transfer start date'
        }
        if (transferEndDate && workEndDate && transferEndDate >= workEndDate) {
            errors.transferEnd = 'Must be before work end date'
        }

        if (
            destUnitReadyDate &&
            workStartDate &&
            destUnitReadyDate.startOf('day') < workStartDate.startOf('day')
        ) {
            errors.destUnitReady = 'Must be on or after work start date'
        }
        if (
            destUnitReadyDate &&
            workEndDate &&
            destUnitReadyDate.startOf('day') > workEndDate.startOf('day')
        ) {
            errors.destUnitReady = 'Must be on or before work end date'
        }
        if (
            dtUnitReadyDate &&
            workStartDate &&
            dtUnitReadyDate.startOf('day') < workStartDate.startOf('day')
        ) {
            errors.dtUnitReady = 'Must be on or after work start date'
        }
        if (
            destUnitReadyDate &&
            dtUnitReadyDate &&
            dtUnitReadyDate.startOf('day') <= destUnitReadyDate.startOf('day')
        ) {
            errors.dtUnitReady = 'Must be after the destination unit ready date'
        }
        if (
            dtUnitReadyDate &&
            workEndDate &&
            dtUnitReadyDate.startOf('day') > workEndDate.startOf('day')
        ) {
            errors.dtUnitReady = 'Must be on or before work end date'
        }

        return errors
    }, [
        workStartDate,
        workEndDate,
        transferStartDate,
        transferEndDate,
        destUnitReadyDate,
        dtUnitReadyDate,
        scheduleStartDate,
        scheduleEndDate,
    ])

    // Ref to flush pending unit selection changes from UnitSelector
    const flushUnitSelectionRef = useRef<(() => void) | null>(null)
    const initializedSelectionKeyRef = useRef<string | null>(null)
    // True when we seeded selection while included units were still empty
    // (e.g. duplicate-from cache not yet applied). Allows a one-time catch-up.
    const seededEmptyIncludedRef = useRef(false)

    const popoverState = usePopover()
    const phaseTemplateUnits = useMemo<TemplateUnit[]>(() => {
        if (!ezSchedule) return []
        return (
            (phaseId === undefined
                ? ezSchedule.template_units
                : selectedPhase?.template_units) ?? []
        )
    }, [ezSchedule, phaseId, selectedPhase])
    const disabledUnitReasonById = useMemo<Record<number, string>>(() => {
        if (!ezSchedule.use_multi_phase || phaseId === undefined) {
            return {}
        }

        const currentPhaseUnitIds = new Set<number>(
            phaseTemplateUnits
                .filter((templateUnit) => templateUnit.is_included)
                .map((templateUnit) => templateUnit.unit.id),
        )

        const phaseLabelById = new Map<number, string>()
        ezSchedule.schedule_phases.forEach((schedulePhase) => {
            phaseLabelById.set(
                schedulePhase.id,
                `Phase ${schedulePhase.phase_index}`,
            )
        })

        const unitLabelsMap = new Map<number, Set<string>>()
        ezSchedule.phases.forEach((phase) => {
            if (phase.phase.id === phaseId) return

            const phaseLabel =
                phaseLabelById.get(phase.phase.id) ??
                `Phase ${phase.phase.phase_index}`

            phase.template_units.forEach((templateUnit) => {
                if (
                    !templateUnit.is_included ||
                    currentPhaseUnitIds.has(templateUnit.unit.id)
                ) {
                    return
                }

                const unitId = templateUnit.unit.id
                if (!unitLabelsMap.has(unitId)) {
                    unitLabelsMap.set(unitId, new Set<string>())
                }
                unitLabelsMap.get(unitId)?.add(phaseLabel)
            })
        })

        const disabledReasonMap: Record<number, string> = {}
        unitLabelsMap.forEach((phaseLabels, unitId) => {
            disabledReasonMap[unitId] = Array.from(phaseLabels).join(', ')
        })

        return disabledReasonMap
    }, [
        ezSchedule.use_multi_phase,
        ezSchedule.schedule_phases,
        ezSchedule.phases,
        phaseId,
        phaseTemplateUnits,
    ])
    const excludedUnitIds = useMemo<Set<number>>(() => {
        return new Set<number>(
            Object.keys(disabledUnitReasonById).map((unitId) => Number(unitId)),
        )
    }, [disabledUnitReasonById])
    const phaseTemplateServices = useMemo(() => {
        if (!ezSchedule) return []
        if (!selectedPhase) return []
        return selectedPhase.template_services ?? []
    }, [ezSchedule, phaseId, selectedPhase])
    const initialSelectedUnitCount = useMemo(() => {
        return phaseTemplateUnits.filter((tu) => tu.is_included).length
    }, [phaseTemplateUnits])

    // Initialize unit selection from the currently selected phase
    useEffect(() => {
        if (!ezSchedule) return
        const initializationKey = `${ezSchedule.id}:${phaseId ?? 'base'}`
        const selectedUnits = phaseTemplateUnits
            .filter((tu) => tu.is_included)
            .map((filteredUnit) => filteredUnit.unit)

        if (initializedSelectionKeyRef.current !== initializationKey) {
            initializedSelectionKeyRef.current = initializationKey
            seededEmptyIncludedRef.current = selectedUnits.length === 0
            setSelection(selectedUnits)
            return
        }

        // Catch-up after duplicate/refetch: first paint had no included units
        if (seededEmptyIncludedRef.current && selectedUnits.length > 0) {
            seededEmptyIncludedRef.current = false
            setSelection(selectedUnits)
        }
    }, [ezSchedule, phaseId, phaseTemplateUnits, setSelection])

    const availableServices = useMemo(() => {
        return serviceList.filter((service) => {
            return vendorList.some((vendor) =>
                vendor.services.some((s) => s.id === service.id),
            )
        })
    }, [vendorList, serviceList])

    const phaseSelectedUnitsCount = useMemo<number>(() => {
        return finderSelection.unit.length
    }, [finderSelection.unit.length])

    const projectSelectedUnitsCount = useMemo<number>(() => {
        return ezSchedule.template_units.filter(
            (templateUnit: TemplateUnit) => templateUnit.is_included,
        ).length
    }, [ezSchedule.template_units])

    const [dtChains, setDtChains] = useState<
        DoubleTransferChainResponse['chains']
    >([])

    useEffect(() => {
        if (!ezSchedule?.id) {
            setDtChains([])
            return
        }
        let cancelled = false
        axiosInstance
            .get<DoubleTransferChainResponse>(
                `workorder/schedule/${ezSchedule.id}/double-transfer-chains/`,
            )
            .then((response) => {
                if (!cancelled) {
                    setDtChains(response.data?.chains ?? [])
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setDtChains([])
                }
            })
        return () => {
            cancelled = true
        }
    }, [ezSchedule?.id])

    const selectedUnitIdSet = useMemo(() => {
        return new Set(
            Object.values(finderSelection.unit)
                .filter((unit): unit is Unit => unit !== undefined)
                .map((unit) => unit.id),
        )
    }, [finderSelection.unit])

    const linkedUnitIdsByUnitId = useMemo<Record<number, number[]>>(() => {
        const linked: Record<number, number[]> = {}
        dtChains.forEach((chain) => {
            if (chain.skip) {
                return
            }
            const unitIds = [
                chain.dest.area?.unit_id,
                chain.dt.area?.unit_id,
                chain.origin.area?.unit_id,
            ].filter((unitId): unitId is number => unitId != null)
            const unique = Array.from(new Set(unitIds))
            unique.forEach((unitId) => {
                linked[unitId] = unique
            })
        })
        return linked
    }, [dtChains])

    const includedDtUnitNames = useMemo(() => {
        const names: string[] = []
        dtChains.forEach((chain) => {
            const dtUnitId = chain.dt.area?.unit_id
            if (dtUnitId != null && selectedUnitIdSet.has(dtUnitId)) {
                names.push(chain.dt.area?.unit_name ?? `Unit ${dtUnitId}`)
            }
        })
        return names
    }, [dtChains, selectedUnitIdSet])

    const hasIncludedDt = includedDtUnitNames.length > 0
    const [dtReadyModalOpen, setDtReadyModalOpen] = useState(false)

    const nonStandaloneServiceCount = useMemo(() => {
        return phaseTemplateServices.filter((service) => !service.stand_alone)
            .length
    }, [phaseTemplateServices])

    const minDestUnitReady = useMemo((): DateTime | null => {
        if (!workStartDate || nonStandaloneServiceCount < 1) {
            return null
        }
        return workStartDate.startOf('day').plus({
            days: nonStandaloneServiceCount,
        })
    }, [workStartDate, nonStandaloneServiceCount])

    const minDtUnitReady = useMemo((): DateTime | null => {
        const destReady = destUnitReadyDate?.startOf('day') ?? minDestUnitReady
        if (!destReady || nonStandaloneServiceCount < 1) {
            return null
        }
        return destReady.plus({ days: nonStandaloneServiceCount + 1 })
    }, [destUnitReadyDate, minDestUnitReady, nonStandaloneServiceCount])

    useEffect(() => {
        const collectUnits = (folder: Folder): Unit[] => {
            return folder.units.concat(
                ...folder.children.map((child) => collectUnits(child)),
            )
        }
        const unitById = new Map(
            collectUnits(infrastructure).map((unit) => [unit.id, unit]),
        )
        const current = Object.values(finderSelection.unit).filter(
            (unit): unit is Unit => unit !== undefined,
        )
        const currentIds = new Set(current.map((unit) => unit.id))
        const toAdd: Unit[] = []
        current.forEach((unit) => {
            const linked = linkedUnitIdsByUnitId[unit.id]
            if (!linked) {
                return
            }
            linked.forEach((linkedId) => {
                if (currentIds.has(linkedId) || excludedUnitIds.has(linkedId)) {
                    return
                }
                const mate = unitById.get(linkedId)
                if (mate) {
                    toAdd.push(mate)
                    currentIds.add(linkedId)
                }
            })
        })
        if (toAdd.length > 0) {
            setSelection([...current, ...toAdd])
        }
    }, [linkedUnitIdsByUnitId, infrastructure, excludedUnitIds])

    const unresolvedDtWarning = useMemo(() => {
        const names: string[] = []
        dtChains.forEach((chain) => {
            if (!chain.skip) {
                return
            }
            const dtUnitId = chain.dt.area?.unit_id
            if (dtUnitId != null && selectedUnitIdSet.has(dtUnitId)) {
                names.push(chain.dt.area?.unit_name ?? `Unit ${dtUnitId}`)
            }
        })
        if (names.length === 0) {
            return null
        }
        return `Could not resolve the double-transfer chain for ${names.join(
            ', ',
        )}. Include dest and origin units yourself if you still want them turned.`
    }, [dtChains, selectedUnitIdSet])

    const handleProceed = () => {
        // Flush any pending unit selection changes before proceeding
        flushUnitSelectionRef.current?.()
        onPhaseChange('review')
    }

    const proceedViolations = useMemo<string[]>(() => {
        const violations: string[] = []

        if (workStartDate === null) {
            violations.push('Set a Work Start Date.')
        }

        if (workEndDate === null) {
            violations.push('Set a Work End Date.')
        }

        if (ezScheduleLoading) {
            violations.push('Schedule settings are still loading.')
        }

        if (phaseSelectedUnitsCount === 0) {
            violations.push('Select at least one unit.')
        }

        if (phaseTemplateServices.length === 0) {
            violations.push('Add at least one service.')
        }

        const servicesMissingVendors = phaseTemplateServices.filter(
            (templateService) =>
                templateService.template_service_vendors.length === 0,
        )
        if (servicesMissingVendors.length > 0) {
            const previewServiceNames = servicesMissingVendors
                .slice(0, 3)
                .map((templateService) => templateService.service.name)
                .join(', ')
            const remainingServiceCount = servicesMissingVendors.length - 3
            violations.push(
                remainingServiceCount > 0
                    ? `Assign at least one vendor to every service (${previewServiceNames}, +${remainingServiceCount} more).`
                    : `Assign at least one vendor to every service (${previewServiceNames}).`,
            )
        }

        const dateValidationMessages = (Object.entries(
            dateValidationErrors,
        ) as [keyof typeof dateValidationErrors, string | undefined][])
            .filter(
                ([key, message]) =>
                    Boolean(message) &&
                    key !== 'destUnitReady' &&
                    key !== 'dtUnitReady',
            )
            .map(([, message]) => message as string)
        if (dateValidationMessages.length > 0) {
            violations.push(...dateValidationMessages)
        }

        const destReadyTooEarly = Boolean(
            destUnitReadyDate &&
                minDestUnitReady &&
                destUnitReadyDate.startOf('day') <
                    minDestUnitReady.startOf('day'),
        )
        const dtReadyTooEarly = Boolean(
            destUnitReadyDate &&
                dtUnitReadyDate &&
                minDtUnitReady &&
                dtUnitReadyDate.startOf('day') < minDtUnitReady.startOf('day'),
        )
        const destDtViolation =
            hasIncludedDt &&
            (!destUnitReadyDate ||
                !dtUnitReadyDate ||
                destReadyTooEarly ||
                dtReadyTooEarly ||
                Boolean(dateValidationErrors.destUnitReady) ||
                Boolean(dateValidationErrors.dtUnitReady))
        if (destDtViolation) {
            violations.push(
                'You have double transfers in your schedule. You need to click the button to manage them.',
            )
        }

        return Array.from(new Set(violations))
    }, [
        ezScheduleLoading,
        phaseSelectedUnitsCount,
        phaseTemplateServices,
        workStartDate,
        workEndDate,
        dateValidationErrors,
        hasIncludedDt,
        destUnitReadyDate,
        dtUnitReadyDate,
        minDestUnitReady,
        minDtUnitReady,
    ])

    const proceedDisabled = proceedViolations.length > 0
    const addServiceDisabledMessage: string =
        workStartDate === null
            ? 'Set a Work Start Date to add services.'
            : workEndDate === null
            ? 'Set a Work End Date to add services.'
            : ''
    const isAddServiceDisabled: boolean = addServiceDisabledMessage.length > 0

    const [rulesDialogOpen, setRulesDialogOpen] = useState<boolean>(false)
    const [
        settingsHelpDialogOpen,
        setSettingsHelpDialogOpen,
    ] = useState<boolean>(false)
    const [
        selectedCommonAreaRule,
        setSelectedCommonAreaRule,
    ] = useState<CommonAreaRule>(
        ezSchedule?.common_area_rule ?? CommonAreaRule.NO_RULE,
    )
    const [
        selectedBathAreaRule,
        setSelectedBathAreaRule,
    ] = useState<BathAreaRule>(
        ezSchedule?.bath_area_rule ?? BathAreaRule.NO_RULE,
    )
    const [
        selectedUseMultiPhaseTurn,
        setSelectedUseMultiPhaseTurn,
    ] = useState<boolean>(ezSchedule?.use_multi_phase ?? false)
    const [
        selectedUseOrderControl,
        setSelectedUseOrderControl,
    ] = useState<boolean>(ezSchedule?.use_order_control ?? false)

    useEffect(() => {
        if (!ezSchedule) return
        setSelectedCommonAreaRule(ezSchedule.common_area_rule)
        setSelectedBathAreaRule(ezSchedule.bath_area_rule)
        setSelectedUseMultiPhaseTurn(ezSchedule.use_multi_phase ?? false)
        setSelectedUseOrderControl(ezSchedule.use_order_control ?? false)
    }, [
        ezSchedule?.id,
        ezSchedule?.common_area_rule,
        ezSchedule?.bath_area_rule,
        ezSchedule?.use_multi_phase,
        ezSchedule?.use_order_control,
    ])

    const handleOpenRulesDialog = () => {
        setSelectedCommonAreaRule(ezSchedule.common_area_rule)
        setSelectedBathAreaRule(ezSchedule.bath_area_rule)
        setSelectedUseMultiPhaseTurn(ezSchedule.use_multi_phase ?? false)
        setSelectedUseOrderControl(ezSchedule.use_order_control ?? false)
        setRulesDialogOpen(true)
    }

    const handleSaveRules = () => {
        updateEzSchedule({
            common_area_rule: selectedCommonAreaRule,
            bath_area_rule: selectedBathAreaRule,
            use_multi_phase: selectedUseMultiPhaseTurn,
            use_order_control: selectedUseOrderControl,
        })
        setRulesDialogOpen(false)
    }

    const renderRuleOptionLabel = (label: string, description: string) => (
        <span style={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
                component="span"
                style={{ fontWeight: 550, fontSize: '.9rem', lineHeight: 1.2 }}
            >
                {label}
            </Typography>
            <Typography
                component="span"
                style={{
                    fontSize: '0.82rem',
                    lineHeight: 1.3,
                    color: 'rgba(0,0,0,0.6)',
                }}
            >
                {description}
            </Typography>
        </span>
    )

    if (ezSchedule === undefined || ezSchedule === null || ezScheduleLoading) {
        return (
            <Container className={classes.root}>
                <Container className={classes.loadingContainer}>
                    <CircularProgress />
                </Container>
            </Container>
        )
    }

    return (
        <Container className={classes.root}>
            <Paper
                elevation={0}
                style={{
                    width: '100%',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    overflow: 'hidden',
                }}
            >
                <Grid container wrap="nowrap" className={classes.gridContainer}>
                    {/* Unit Selection Panel */}
                    <Grid
                        item
                        xs={3}
                        style={{
                            display: 'flex',
                            minHeight: 0,
                            overflow: 'hidden',
                        }}
                    >
                        <Container className={classes.unitSelectionPanel}>
                            <Typography
                                variant="h6"
                                className={classes.sectionTitle}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                }}
                            >
                                Select Units
                                <Tooltip title="Selecting any unit in a double-transfer chain also selects dest, DT, and origin units. Deselecting one deselects the whole chain.">
                                    <InfoOutlinedIcon
                                        fontSize="small"
                                        color="action"
                                    />
                                </Tooltip>
                            </Typography>
                            {unresolvedDtWarning && (
                                <Typography
                                    variant="body2"
                                    style={{
                                        color: theme.palette.error.main,
                                        marginBottom: theme.spacing(1),
                                    }}
                                >
                                    {unresolvedDtWarning}
                                </Typography>
                            )}
                            {ezSchedule.use_multi_phase ? (
                                <>
                                    <Typography
                                        variant="body1"
                                        gutterBottom
                                        className={classes.unitCountText}
                                    >
                                        Project units selected:{' '}
                                        {projectSelectedUnitsCount}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        gutterBottom
                                        className={classes.unitCountText}
                                    >
                                        Units selected in this phase:{' '}
                                        {phaseSelectedUnitsCount}
                                    </Typography>
                                </>
                            ) : (
                                <Typography
                                    variant="body1"
                                    gutterBottom
                                    className={classes.unitCountText}
                                >
                                    Units selected: {phaseSelectedUnitsCount}
                                </Typography>
                            )}
                            <UnitSelector
                                infrastructure={infrastructure}
                                finderSelection={finderSelection}
                                onUnitSelectionChange={setSelection}
                                setTemplateUnits={setTemplateUnits}
                                flushRef={flushUnitSelectionRef}
                                phaseId={phaseId}
                                initialSelectedUnitCount={
                                    initialSelectedUnitCount
                                }
                                disabledUnitIds={excludedUnitIds}
                                disabledUnitReasonById={disabledUnitReasonById}
                                linkedUnitIdsByUnitId={linkedUnitIdsByUnitId}
                            />
                        </Container>
                    </Grid>

                    {/* Configuration Panel - Timeline, Rules, Services */}
                    <Grid
                        item
                        xs={9}
                        style={{
                            display: 'flex',
                            minHeight: 0,
                            overflow: 'hidden',
                        }}
                    >
                        <Container className={classes.configPanel}>
                            <Container
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flexShrink: 0,
                                    gap: theme.spacing(1),
                                    marginBottom: theme.spacing(1),
                                    borderBottom: `1px solid ${theme.palette.grey[300]}`,
                                    paddingBottom: theme.spacing(1),
                                    marginTop: theme.spacing(0.75),
                                }}
                            >
                                <Container
                                    className={classes.headerControlsRow}
                                >
                                    <Container
                                        className={`${classes.headerDateGroup} ${classes.headerSectionCard}`}
                                    >
                                        <Typography
                                            className={
                                                classes.headerSectionTitle
                                            }
                                        >
                                            Project Timeline
                                        </Typography>
                                        <Container
                                            className={classes.headerDateStack}
                                        >
                                            <Container
                                                className={
                                                    classes.headerDateField
                                                }
                                            >
                                                <KeyboardDatePicker
                                                    label="Work Start Date"
                                                    InputLabelProps={{
                                                        style: {
                                                            fontWeight: 700,
                                                        },
                                                    }}
                                                    fullWidth
                                                    format="MM/dd/yyyy"
                                                    placeholder="Select date"
                                                    value={
                                                        workStartDate?.setZone(
                                                            'local',
                                                            {
                                                                keepLocalTime: true,
                                                            },
                                                        ) ?? null
                                                    }
                                                    initialFocusedDate={
                                                        workStartInitialFocusedDate?.setZone(
                                                            'local',
                                                            {
                                                                keepLocalTime: true,
                                                            },
                                                        ) ?? undefined
                                                    }
                                                    error={
                                                        !!dateValidationErrors.workStart
                                                    }
                                                    helperText={
                                                        dateValidationErrors.workStart
                                                    }
                                                    onChange={(date) => {
                                                        if (date === null) {
                                                            updateEzSchedule({
                                                                work_start_date: null,
                                                            })
                                                            return
                                                        }

                                                        const newDate = DateTime.fromJSDate(
                                                            date,
                                                        )
                                                            .setZone(
                                                                currentTimezone,
                                                                {
                                                                    keepLocalTime: true,
                                                                },
                                                            )
                                                            .startOf('day')

                                                        if (newDate.isValid) {
                                                            updateEzSchedule({
                                                                work_start_date: newDate,
                                                            })
                                                        }
                                                    }}
                                                />
                                            </Container>
                                            <Container
                                                className={
                                                    classes.headerDateField
                                                }
                                            >
                                                <KeyboardDatePicker
                                                    label="Work End Date"
                                                    InputLabelProps={{
                                                        style: {
                                                            fontWeight: 700,
                                                        },
                                                    }}
                                                    fullWidth
                                                    format="MM/dd/yyyy"
                                                    placeholder="Select date"
                                                    value={
                                                        workEndDate?.setZone(
                                                            'local',
                                                            {
                                                                keepLocalTime: true,
                                                            },
                                                        ) ?? null
                                                    }
                                                    initialFocusedDate={
                                                        workEndInitialFocusedDate?.setZone(
                                                            'local',
                                                            {
                                                                keepLocalTime: true,
                                                            },
                                                        ) ?? undefined
                                                    }
                                                    error={
                                                        !!dateValidationErrors.workEnd
                                                    }
                                                    helperText={
                                                        dateValidationErrors.workEnd
                                                    }
                                                    onChange={(date) => {
                                                        if (date === null) {
                                                            updateEzSchedule({
                                                                work_end_date: null,
                                                            })
                                                            return
                                                        }

                                                        const newDate = DateTime.fromJSDate(
                                                            date,
                                                        )
                                                            .setZone(
                                                                currentTimezone,
                                                                {
                                                                    keepLocalTime: true,
                                                                },
                                                            )
                                                            .endOf('day')

                                                        if (newDate.isValid) {
                                                            updateEzSchedule({
                                                                work_end_date: newDate,
                                                            })
                                                        }
                                                    }}
                                                />
                                            </Container>
                                        </Container>
                                    </Container>

                                    <Container
                                        className={`${classes.headerDateGroup} ${classes.headerSectionCard}`}
                                    >
                                        <Typography
                                            className={
                                                classes.headerSectionTitle
                                            }
                                        >
                                            Transfer Dates (Optional)
                                        </Typography>
                                        <Typography
                                            className={
                                                classes.headerCaptionText
                                            }
                                        >
                                            If you select one, you must select
                                            both. Same start and end date is
                                            allowed. TIs are scheduled prior to
                                            the start; TOs are scheduled after
                                            the end.
                                        </Typography>
                                        <Container
                                            className={classes.headerDateStack}
                                        >
                                            <Container
                                                className={
                                                    classes.headerDateField
                                                }
                                            >
                                                <KeyboardDatePicker
                                                    label="Transfer Start Date"
                                                    InputLabelProps={{
                                                        style: {
                                                            fontWeight: 700,
                                                        },
                                                    }}
                                                    fullWidth
                                                    format="MM/dd/yyyy"
                                                    placeholder="Select date"
                                                    value={
                                                        transferStartDate?.setZone(
                                                            'local',
                                                            {
                                                                keepLocalTime: true,
                                                            },
                                                        ) ?? null
                                                    }
                                                    initialFocusedDate={
                                                        transferStartInitialFocusedDate?.setZone(
                                                            'local',
                                                            {
                                                                keepLocalTime: true,
                                                            },
                                                        ) ?? undefined
                                                    }
                                                    error={
                                                        !!dateValidationErrors.transferStart
                                                    }
                                                    helperText={
                                                        dateValidationErrors.transferStart
                                                    }
                                                    onChange={(date) => {
                                                        if (date === null) {
                                                            updateEzSchedule({
                                                                transfer_start_date: null,
                                                            })
                                                            return
                                                        }

                                                        const newDate = DateTime.fromJSDate(
                                                            date,
                                                        )
                                                            .setZone(
                                                                currentTimezone,
                                                                {
                                                                    keepLocalTime: true,
                                                                },
                                                            )
                                                            .startOf('day')

                                                        if (newDate.isValid) {
                                                            updateEzSchedule({
                                                                transfer_start_date: newDate.toISO(),
                                                            })
                                                        }
                                                    }}
                                                />
                                            </Container>

                                            <Container
                                                className={
                                                    classes.headerDateField
                                                }
                                            >
                                                <KeyboardDatePicker
                                                    label="Transfer End Date"
                                                    InputLabelProps={{
                                                        style: {
                                                            fontWeight: 700,
                                                        },
                                                    }}
                                                    fullWidth
                                                    format="MM/dd/yyyy"
                                                    placeholder="Select date"
                                                    value={
                                                        transferEndDate?.setZone(
                                                            'local',
                                                            {
                                                                keepLocalTime: true,
                                                            },
                                                        ) ?? null
                                                    }
                                                    initialFocusedDate={
                                                        transferEndInitialFocusedDate?.setZone(
                                                            'local',
                                                            {
                                                                keepLocalTime: true,
                                                            },
                                                        ) ?? undefined
                                                    }
                                                    error={
                                                        !!dateValidationErrors.transferEnd
                                                    }
                                                    helperText={
                                                        dateValidationErrors.transferEnd
                                                    }
                                                    onChange={(date) => {
                                                        if (date === null) {
                                                            updateEzSchedule({
                                                                transfer_end_date: null,
                                                            })
                                                            return
                                                        }

                                                        const newDate = DateTime.fromJSDate(
                                                            date,
                                                        )
                                                            .setZone(
                                                                currentTimezone,
                                                                {
                                                                    keepLocalTime: true,
                                                                },
                                                            )
                                                            .endOf('day')

                                                        if (newDate.isValid) {
                                                            updateEzSchedule({
                                                                transfer_end_date: newDate.toISO(),
                                                            })
                                                        }
                                                    }}
                                                />
                                            </Container>
                                        </Container>
                                    </Container>

                                    {/* Settings Group */}
                                    <Container
                                        className={`${classes.workspaceRulesGroup} ${classes.headerSectionCard}`}
                                    >
                                        <Container
                                            style={{
                                                width: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                marginBottom: theme.spacing(1),
                                                paddingLeft: theme.spacing(1),
                                                paddingRight: theme.spacing(1),
                                            }}
                                        >
                                            <Container
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <SettingsIcon
                                                    style={{ fontSize: 24 }}
                                                />
                                                <Typography
                                                    className={
                                                        classes.headerSectionTitle
                                                    }
                                                >
                                                    Settings
                                                </Typography>
                                            </Container>
                                            <Container
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: theme.spacing(0.5),
                                                }}
                                            >
                                                <Tooltip title="Click to learn more about settings">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            setSettingsHelpDialogOpen(
                                                                true,
                                                            )
                                                        }
                                                        aria-label="Open settings help"
                                                    >
                                                        <HelpOutlineIcon
                                                            style={{
                                                                fontSize: 24,
                                                            }}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Edit settings">
                                                    <Button
                                                        onClick={
                                                            handleOpenRulesDialog
                                                        }
                                                        startIcon={<EditIcon />}
                                                        variant="outlined"
                                                        size="small"
                                                        style={{
                                                            minWidth: 'auto',
                                                            paddingLeft: theme.spacing(
                                                                1,
                                                            ),
                                                            paddingRight: theme.spacing(
                                                                1,
                                                            ),
                                                            borderWidth: 1.5,
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        Edit
                                                    </Button>
                                                </Tooltip>
                                            </Container>
                                        </Container>
                                        {/* Settings Labels */}
                                        <Container
                                            style={{
                                                width: '100%',
                                                display: 'grid',
                                                gridTemplateColumns: '1fr auto',
                                                gridTemplateRows:
                                                    'auto auto auto auto',
                                                columnGap: theme.spacing(1.5),
                                                rowGap: theme.spacing(0.5),
                                                alignItems: 'center',
                                                paddingRight: theme.spacing(
                                                    1.5,
                                                ),
                                                marginBottom: theme.spacing(1),
                                            }}
                                        >
                                            <Container
                                                style={{
                                                    gridColumn: '1 / 2',
                                                    gridRow: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    minWidth: 0,
                                                }}
                                            >
                                                <Typography
                                                    style={{
                                                        fontSize: '0.78rem',
                                                        whiteSpace: 'nowrap',
                                                        marginLeft: theme.spacing(
                                                            1,
                                                        ),
                                                    }}
                                                >
                                                    Common Area Rule
                                                </Typography>
                                            </Container>
                                            <Typography
                                                style={{
                                                    gridColumn: '2 / 3',
                                                    gridRow: 1,
                                                    fontSize: '0.84rem',
                                                    fontWeight: 600,
                                                    whiteSpace: 'nowrap',
                                                    justifySelf: 'start',
                                                }}
                                            >
                                                {
                                                    COMMON_AREA_RULE_DETAILS[
                                                        ezSchedule
                                                            .common_area_rule
                                                    ].label
                                                }
                                            </Typography>
                                            <Container
                                                style={{
                                                    gridColumn: '1 / 2',
                                                    gridRow: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    minWidth: 0,
                                                }}
                                            >
                                                <Typography
                                                    style={{
                                                        fontSize: '0.78rem',
                                                        whiteSpace: 'nowrap',
                                                        marginLeft: theme.spacing(
                                                            1,
                                                        ),
                                                    }}
                                                >
                                                    Bath Area Rule
                                                </Typography>
                                            </Container>
                                            <Typography
                                                style={{
                                                    gridColumn: '2 / 3',
                                                    gridRow: 2,
                                                    fontSize: '0.84rem',
                                                    fontWeight: 600,
                                                    whiteSpace: 'nowrap',
                                                    justifySelf: 'start',
                                                }}
                                            >
                                                {
                                                    BATH_AREA_RULE_DETAILS[
                                                        ezSchedule
                                                            .bath_area_rule
                                                    ].label
                                                }
                                            </Typography>
                                            <Container
                                                style={{
                                                    gridColumn: '1 / 2',
                                                    gridRow: 3,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    minWidth: 0,
                                                }}
                                            >
                                                <Typography
                                                    style={{
                                                        fontSize: '0.78rem',
                                                        whiteSpace: 'nowrap',
                                                        marginLeft: theme.spacing(
                                                            1,
                                                        ),
                                                    }}
                                                >
                                                    Multi-Phase Turn
                                                </Typography>
                                            </Container>
                                            <Typography
                                                style={{
                                                    gridColumn: '2 / 3',
                                                    gridRow: 3,
                                                    fontSize: '0.84rem',
                                                    fontWeight: 600,
                                                    whiteSpace: 'nowrap',
                                                    justifySelf: 'start',
                                                }}
                                            >
                                                {ezSchedule.use_multi_phase
                                                    ? 'Yes'
                                                    : 'No'}
                                            </Typography>
                                            <Container
                                                style={{
                                                    gridColumn: '1 / 2',
                                                    gridRow: 4,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    minWidth: 0,
                                                }}
                                            >
                                                <Typography
                                                    style={{
                                                        fontSize: '0.78rem',
                                                        whiteSpace: 'nowrap',
                                                        marginLeft: theme.spacing(
                                                            1,
                                                        ),
                                                    }}
                                                >
                                                    Order Control
                                                </Typography>
                                            </Container>
                                            <Typography
                                                style={{
                                                    gridColumn: '2 / 3',
                                                    gridRow: 4,
                                                    fontSize: '0.84rem',
                                                    fontWeight: 600,
                                                    whiteSpace: 'nowrap',
                                                    justifySelf: 'start',
                                                }}
                                            >
                                                {ezSchedule.use_order_control
                                                    ? 'Yes'
                                                    : 'No'}
                                            </Typography>
                                        </Container>
                                    </Container>

                                    {/* Proceed to Review Button */}
                                    <Container
                                        className={classes.proceedButtonGroup}
                                    >
                                        <Button
                                            onClick={handleProceed}
                                            className={classes.proceedButton}
                                            startIcon={<ArrowForwardIcon />}
                                            disabled={proceedDisabled}
                                        >
                                            Proceed to Review
                                        </Button>
                                        {hasIncludedDt && (
                                            <Button
                                                onClick={() =>
                                                    setDtReadyModalOpen(true)
                                                }
                                                className={
                                                    classes.proceedButton
                                                }
                                                disabled={
                                                    nonStandaloneServiceCount <
                                                    1
                                                }
                                            >
                                                Manage Double Transfers
                                            </Button>
                                        )}
                                        {hasIncludedDt &&
                                            nonStandaloneServiceCount < 1 && (
                                                <Typography
                                                    variant="body2"
                                                    style={{ marginTop: 8 }}
                                                >
                                                    Select all your services
                                                    before working on the double
                                                    transfer window.
                                                </Typography>
                                            )}
                                        {proceedViolations.length > 0 && (
                                            <Container
                                                className={
                                                    classes.proceedViolationsCard
                                                }
                                            >
                                                <ul
                                                    className={
                                                        classes.proceedViolationsList
                                                    }
                                                >
                                                    {proceedViolations.map(
                                                        (violation) => (
                                                            <li
                                                                key={violation}
                                                                className={
                                                                    classes.proceedViolationsItem
                                                                }
                                                            >
                                                                {violation}
                                                            </li>
                                                        ),
                                                    )}
                                                </ul>
                                            </Container>
                                        )}
                                    </Container>
                                </Container>
                            </Container>

                            {/* Settings Dialog */}
                            <Dialog
                                open={rulesDialogOpen}
                                onClose={() => setRulesDialogOpen(false)}
                                maxWidth="md"
                                fullWidth
                            >
                                <DialogContent>
                                    {/* Workspace Settings Section */}
                                    <Container
                                        direction="column"
                                        style={{
                                            borderRadius: 8,
                                            padding: '20px 24px',
                                            marginBottom: 0,
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            style={{
                                                fontWeight: 700,
                                                marginBottom: 12,
                                            }}
                                        >
                                            Workspace Settings
                                        </Typography>

                                        <Container
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 8,
                                                marginBottom: 16,
                                                padding: '12px 16px',
                                                backgroundColor: '#ffffff',
                                                borderRadius: 6,
                                                border: '1px solid #e0e0e0',
                                            }}
                                        >
                                            <DialogContentText
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 10,
                                                    margin: 0,
                                                }}
                                            >
                                                <Public
                                                    style={{
                                                        fontSize: 22,
                                                        color: '#5c6bc0',
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                Common Area and Bath Area rules
                                                apply to your entire workspace.
                                            </DialogContentText>
                                            <DialogContentText
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 10,
                                                    margin: 0,
                                                }}
                                            >
                                                <ErrorOutline
                                                    style={{
                                                        fontSize: 22,
                                                        color: '#f9a825',
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                Modifying these rules here will
                                                affect how statuses are set in
                                                other places.
                                            </DialogContentText>
                                            <DialogContentText
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 10,
                                                    margin: 0,
                                                }}
                                            >
                                                <Lock
                                                    style={{
                                                        fontSize: 22,
                                                        color: '#ef5350',
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                However, selecting a rule here
                                                will <strong>not affect</strong>{' '}
                                                locked statuses.
                                            </DialogContentText>
                                        </Container>

                                        <Container
                                            className={classes.rulesDialogGroup}
                                        >
                                            <Typography
                                                style={{
                                                    fontWeight: 700,
                                                    textAlign: 'left',
                                                    marginBottom: 6,
                                                    fontSize: '1.05rem',
                                                }}
                                            >
                                                Common Area Rule
                                            </Typography>
                                            <FormControl
                                                component="fieldset"
                                                className={
                                                    classes.rulesDialogFormControl
                                                }
                                            >
                                                <RadioGroup
                                                    value={
                                                        selectedCommonAreaRule
                                                    }
                                                    onChange={(e) =>
                                                        setSelectedCommonAreaRule(
                                                            e.target
                                                                .value as CommonAreaRule,
                                                        )
                                                    }
                                                >
                                                    {Object.entries(
                                                        COMMON_AREA_RULE_DETAILS,
                                                    ).map(
                                                        ([value, details]) => (
                                                            <FormControlLabel
                                                                key={value}
                                                                value={value}
                                                                style={{
                                                                    alignItems:
                                                                        'flex-start',
                                                                    marginTop: 2,
                                                                    marginBottom: 2,
                                                                }}
                                                                control={
                                                                    <Radio />
                                                                }
                                                                label={renderRuleOptionLabel(
                                                                    details.label,
                                                                    details.description,
                                                                )}
                                                            />
                                                        ),
                                                    )}
                                                </RadioGroup>
                                            </FormControl>
                                        </Container>

                                        <Container
                                            className={classes.rulesDialogGroup}
                                        >
                                            <Typography
                                                style={{
                                                    fontWeight: 700,
                                                    textAlign: 'left',
                                                    marginBottom: 6,
                                                    fontSize: '1.05rem',
                                                }}
                                            >
                                                Bath Area Rule
                                            </Typography>
                                            <FormControl
                                                component="fieldset"
                                                className={
                                                    classes.rulesDialogFormControl
                                                }
                                            >
                                                <RadioGroup
                                                    value={selectedBathAreaRule}
                                                    onChange={(e) =>
                                                        setSelectedBathAreaRule(
                                                            e.target
                                                                .value as BathAreaRule,
                                                        )
                                                    }
                                                >
                                                    {Object.entries(
                                                        BATH_AREA_RULE_DETAILS,
                                                    ).map(
                                                        ([value, details]) => (
                                                            <FormControlLabel
                                                                key={value}
                                                                value={value}
                                                                style={{
                                                                    alignItems:
                                                                        'flex-start',
                                                                    marginTop: 2,
                                                                    marginBottom: 2,
                                                                }}
                                                                control={
                                                                    <Radio />
                                                                }
                                                                label={renderRuleOptionLabel(
                                                                    details.label,
                                                                    details.description,
                                                                )}
                                                            />
                                                        ),
                                                    )}
                                                </RadioGroup>
                                            </FormControl>
                                        </Container>
                                    </Container>

                                    <Divider style={{ margin: '20px 0' }} />

                                    {/* Project Settings Section */}
                                    <Container
                                        direction="column"
                                        style={{
                                            padding: '0 24px 8px',
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            style={{
                                                fontWeight: 700,
                                                marginBottom: 12,
                                            }}
                                        >
                                            Project Settings
                                        </Typography>
                                        <Container
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                            }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={
                                                            selectedUseMultiPhaseTurn
                                                        }
                                                        onChange={(
                                                            _,
                                                            checked,
                                                        ) => {
                                                            setSelectedUseMultiPhaseTurn(
                                                                checked,
                                                            )
                                                        }}
                                                        color="primary"
                                                    />
                                                }
                                                label="Use Multi Phase Turn"
                                            />
                                            <DialogContentText
                                                style={{
                                                    marginTop: -4,
                                                    marginBottom: 10,
                                                    marginLeft: 52,
                                                }}
                                            >
                                                Enables scheduling multiple
                                                turns, within the same project.
                                            </DialogContentText>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={
                                                            selectedUseOrderControl
                                                        }
                                                        onChange={(
                                                            _,
                                                            checked,
                                                        ) => {
                                                            setSelectedUseOrderControl(
                                                                checked,
                                                            )
                                                        }}
                                                        color="primary"
                                                    />
                                                }
                                                label="Use Order Control"
                                            />
                                            <DialogContentText
                                                style={{
                                                    marginTop: -4,
                                                    marginBottom: 0,
                                                    marginLeft: 52,
                                                }}
                                            >
                                                Limits workorder scheduling so
                                                only the first service for a
                                                unit is scheduled with dates.
                                                Following services are scheduled
                                                when the prior service is
                                                completed.
                                            </DialogContentText>
                                        </Container>
                                    </Container>
                                </DialogContent>
                                <DialogActions>
                                    <Button
                                        onClick={() =>
                                            setRulesDialogOpen(false)
                                        }
                                        color="primary"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleSaveRules}
                                        color="primary"
                                        variant="contained"
                                    >
                                        Save
                                    </Button>
                                </DialogActions>
                            </Dialog>
                            <Dialog
                                open={settingsHelpDialogOpen}
                                onClose={() => setSettingsHelpDialogOpen(false)}
                                maxWidth="sm"
                                fullWidth
                            >
                                <DialogTitle>Settings Help</DialogTitle>
                                <DialogContent>
                                    <Typography
                                        style={{
                                            fontWeight: 700,
                                            marginBottom: 4,
                                        }}
                                    >
                                        Common Area Rule
                                    </Typography>
                                    <DialogContentText>
                                        Controls how common area statuses are
                                        set, based on bed statuses.
                                    </DialogContentText>
                                    <Typography
                                        style={{
                                            fontWeight: 700,
                                            marginBottom: 4,
                                        }}
                                    >
                                        Bath Area Rule
                                    </Typography>
                                    <DialogContentText>
                                        Controls how bath area statuses are set,
                                        based on mapped bed statuses.
                                    </DialogContentText>
                                    <Typography
                                        style={{
                                            fontWeight: 700,
                                            marginBottom: 4,
                                        }}
                                    >
                                        Multi-Phase Turn
                                    </Typography>
                                    <DialogContentText>
                                        Enables scheduling multiple turns,
                                        within the same project.
                                    </DialogContentText>
                                    <Typography
                                        style={{
                                            fontWeight: 700,
                                            marginBottom: 4,
                                        }}
                                    >
                                        Order Control
                                    </Typography>
                                    <DialogContentText>
                                        Limits workorder scheduling so only the
                                        first service for a unit is scheduled
                                        with dates. Following services are
                                        scheduled when the prior service is
                                        completed.
                                    </DialogContentText>
                                </DialogContent>
                                <DialogActions>
                                    <Button
                                        onClick={() =>
                                            setSettingsHelpDialogOpen(false)
                                        }
                                        color="primary"
                                        variant="contained"
                                    >
                                        Got it
                                    </Button>
                                </DialogActions>
                            </Dialog>

                            {/* Row 3: Services Section (Full Width) */}
                            <Container className={classes.serviceContainer}>
                                <Container
                                    className={classes.serviceTitleContainer}
                                >
                                    <Typography
                                        variant="h6"
                                        className={classes.sectionTitle}
                                    >
                                        Select Services
                                    </Typography>
                                    <Tooltip
                                        title={addServiceDisabledMessage}
                                        disableHoverListener={
                                            !isAddServiceDisabled
                                        }
                                    >
                                        <span>
                                            <Button
                                                onClick={
                                                    popoverState.handleOpen
                                                }
                                                className={
                                                    classes.addServiceButton
                                                }
                                                startIcon={<AddIcon />}
                                                disabled={isAddServiceDisabled}
                                            >
                                                Add Service
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Container>

                                <Container
                                    className={classes.servicesScrollContainer}
                                    scrollY
                                >
                                    <div className={classes.droppableArea}>
                                        {phaseTemplateServices.map(
                                            (templateService, index) => (
                                                <MemoizedServiceRow
                                                    key={templateService.id}
                                                    index={index}
                                                    serviceCount={
                                                        phaseTemplateServices.length
                                                    }
                                                    templateService={
                                                        templateService
                                                    }
                                                    theme={theme}
                                                    vendorList={vendorList}
                                                    deleteTemplateService={
                                                        deleteTemplateService
                                                    }
                                                    updateTemplateService={
                                                        updateTemplateService
                                                    }
                                                    addTemplateServiceVendor={
                                                        addTemplateServiceVendor
                                                    }
                                                    deleteTemplateServiceVendor={
                                                        deleteTemplateServiceVendor
                                                    }
                                                    updateTemplateServiceVendor={
                                                        updateTemplateServiceVendor
                                                    }
                                                />
                                            ),
                                        )}
                                    </div>
                                </Container>
                            </Container>
                        </Container>
                    </Grid>
                </Grid>

                <SelectorPopover
                    theme={theme}
                    popoverState={popoverState}
                    data={availableServices}
                    label="Select Service"
                    onSubmit={(selectedId) => {
                        // Check if service already exists
                        const serviceExists = phaseTemplateServices.some(
                            (ts) => ts.service.id === selectedId,
                        )
                        if (!serviceExists) {
                            addTemplateService({
                                serviceId: selectedId,
                                serviceName:
                                    availableServices.find(
                                        (s) => s.id === selectedId,
                                    )?.name ?? '',
                                scheduleId: ezSchedule.id,
                            })
                        }
                    }}
                />
            </Paper>
            <DoubleTransferChainModal
                open={dtReadyModalOpen}
                schedule={ezSchedule}
                onClose={() => setDtReadyModalOpen(false)}
                editReadyDates
                destUnitReadyDate={selectedPhase?.dest_unit_ready_date}
                dtUnitReadyDate={selectedPhase?.dt_unit_ready_date}
                minDestUnitReady={minDestUnitReady}
                minDtUnitReady={minDtUnitReady}
                maxReadyDate={workEndDate}
                onUpdateReadyDates={updateEzSchedule}
            />
        </Container>
    )
}
